import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getMySubscription, MySubscription } from '../services/subscriptionService';
import { useAuth } from './AuthContext';

interface SubscriptionPlan {
  id: string;
  name: string;
  features: string[];
  monthlyPrice?: number;
  yearlyPrice?: number;
}

interface SubscriptionState {
  isLoading: boolean;
  subscriptionId: string | null;
  status: 'active' | 'trial' | 'expired' | 'cancelled' | 'suspended' | 'pending_payment' | null;
  plan: SubscriptionPlan | null;
  startDate: string | null;
  endDate: string | null;
  daysRemaining: number;
  isExpired: boolean;
  billingCycle: 'monthly' | 'yearly' | null;
  trialEndsAt: string | null;
  hasFullAccess: boolean; // For system admins
  error: string | null;
}

interface SubscriptionContextValue extends SubscriptionState {
  /**
   * Check if the current subscription includes a specific feature.
   * Uses JWT features for instant access (no API call).
   * System admins always return true.
   * Returns false if subscription is expired.
   */
  hasFeature: (feature: string) => boolean;
  /**
   * Refresh subscription data from the server.
   */
  refreshSubscription: () => Promise<void>;
}

const initialState: SubscriptionState = {
  isLoading: true,
  subscriptionId: null,
  status: null,
  plan: null,
  startDate: null,
  endDate: null,
  daysRemaining: 0,
  isExpired: false,
  billingCycle: null,
  trialEndsAt: null,
  hasFullAccess: false,
  error: null,
};

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, decodedToken } = useAuth();
  const [state, setState] = useState<SubscriptionState>(initialState);

  // Get features from JWT token (most efficient - no API call)
  const jwtFeatures = useMemo(() => {
    return decodedToken?.features || [];
  }, [decodedToken]);

  const jwtSubscriptionStatus = useMemo(() => {
    return decodedToken?.subscriptionStatus;
  }, [decodedToken]);

  const jwtSubscriptionEndDate = useMemo(() => {
    return decodedToken?.subscriptionEndDate;
  }, [decodedToken]);

  const fetchSubscription = useCallback(async () => {
    // Not authenticated if no user
    if (!user) {
      setState({ ...initialState, isLoading: false });
      return;
    }

    // If we have JWT data, use it first for immediate state
    if (decodedToken) {
      const isSystemAdmin = decodedToken.isSystemAdmin;
      const isSchoolAdmin = decodedToken.isSchoolAdmin;
      
      if (isSystemAdmin) {
        setState({
          isLoading: false,
          subscriptionId: null,
          status: 'active',
          plan: { id: '', name: 'System Admin', features: ['all'] },
          startDate: null,
          endDate: null,
          daysRemaining: 999,
          isExpired: false,
          billingCycle: null,
          trialEndsAt: null,
          hasFullAccess: true,
          error: null,
        });
        return;
      }

      // Use JWT data for initial state
      const isExpiredFromJWT = jwtSubscriptionStatus === 'expired' ||
        (jwtSubscriptionEndDate && new Date() > new Date(jwtSubscriptionEndDate));

      setState({
        isLoading: false,
        subscriptionId: null,
        status: jwtSubscriptionStatus || null,
        plan: jwtFeatures.length > 0 ? { id: '', name: '', features: jwtFeatures } : null,
        startDate: null,
        endDate: jwtSubscriptionEndDate || null,
        daysRemaining: jwtSubscriptionEndDate 
          ? Math.max(0, Math.ceil((new Date(jwtSubscriptionEndDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
          : 0,
        isExpired: isExpiredFromJWT || false,
        billingCycle: null,
        trialEndsAt: null,
        hasFullAccess: false, // Only System Admin has full access, not School Admin
        error: null,
      });
    }

    // Optionally fetch full subscription details from API for more data
    // This is a non-blocking enhancement
    try {
      const data = await getMySubscription();

      if (data.isSystemAdmin || data.hasFullAccess) {
        setState({
          isLoading: false,
          subscriptionId: null,
          status: 'active',
          plan: { id: '', name: 'System Admin', features: ['all'] },
          startDate: null,
          endDate: null,
          daysRemaining: 999,
          isExpired: false,
          billingCycle: null,
          trialEndsAt: null,
          hasFullAccess: true,
          error: null,
        });
      } else {
        setState({
          isLoading: false,
          subscriptionId: data.subscriptionId || null,
          status: data.status || null,
          plan: data.plan || null,
          startDate: data.startDate || null,
          endDate: data.endDate || null,
          daysRemaining: data.daysRemaining || 0,
          isExpired: data.isExpired || data.status === 'expired',
          billingCycle: data.billingCycle || null,
          trialEndsAt: data.trialEndsAt || null,
          hasFullAccess: false,
          error: null,
        });
      }
    } catch (error: any) {
      // If JWT data exists, don't show error - just use JWT data
      if (jwtFeatures.length > 0 || decodedToken?.isSystemAdmin || decodedToken?.isSchoolAdmin) {
        return; // Already set state from JWT
      }

      console.error('Failed to fetch subscription:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load subscription';
      const errorCode = error.response?.data?.data?.code;
      
      const isNoSubscription = errorCode === 'NO_SUBSCRIPTION' || error.response?.status === 404;
      
      setState({
        ...initialState,
        isLoading: false,
        isExpired: isNoSubscription,
        error: errorMessage,
      });
    }
  }, [user, decodedToken, jwtFeatures, jwtSubscriptionStatus, jwtSubscriptionEndDate]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  /**
   * Check if user has access to a specific feature
   * Uses JWT features first (instant), falls back to state
   * 
   * Note: School Admin has full PERMISSIONS (can do everything within accessible modules),
   *       but is still restricted by SUBSCRIPTION FEATURES (which modules are available)
   *       Only System Admin bypasses subscription feature checks.
   */
  const hasFeature = useCallback((feature: string): boolean => {
    // System admin has access to everything (bypasses subscription)
    if (state.hasFullAccess || decodedToken?.isSystemAdmin) return true;

    // Check JWT features first (most efficient - no API needed)
    if (jwtFeatures.length > 0) {
      // Check expiry from JWT
      if (jwtSubscriptionStatus === 'expired') return false;
      if (jwtSubscriptionEndDate && new Date() > new Date(jwtSubscriptionEndDate)) return false;
      
      return jwtFeatures.includes('all') || jwtFeatures.includes(feature);
    }

    // Fallback to state (from API)
    if (state.isExpired || state.status === 'expired') return false;
    if (!state.plan?.features) return false;
    return state.plan.features.includes('all') || state.plan.features.includes(feature);
  }, [state, decodedToken, jwtFeatures, jwtSubscriptionStatus, jwtSubscriptionEndDate]);

  return (
    <SubscriptionContext.Provider value={{ ...state, hasFeature, refreshSubscription: fetchSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
